import { useState, useEffect, useContext, useRef } from "react";
import { styled } from "@mui/material/styles";
import { Dialog, DialogContent, DialogTitle, CircularProgress } from "@mui/material";
import { ReactComponent as CheckIcon } from "../../../images/svg/tick-circle.svg";
import { ReactComponent as CloseIcon } from "../../../images/svg/close-circle.svg";
import ShopContext from "context/ShopContext";
import { STORAGE_KEYS } from "const";
import {
  syncInitialCategoriesOnly,
  syncInitialProductsOnly,
  syncPriceCategoryItems,
  syncPriceCategories,
  syncCharges,
  syncChargeGroups,
  syncAddonMappings,
} from "helpers/ProductHelper";
import { getCurrentTerminalId, syncTerminalSettings } from "helpers/TermialHelper";
import { loadInvoiceFormatForTerminal } from "helpers/InvoiceHelper";
import ShopService from "services/ShopService";
import CustomerHelper from "helpers/CustomerHelper";
import { loadInitialSalespersons } from "helpers/SalespersonHelper";
import { loadInitialAreasAndTables } from "helpers/TableAreaHelper";
import * as ExpenseHelper from "helpers/ExpenseHelper";
import ShopsHelper from "helpers/ShopsHelper";
import { syncPendingSales } from "helpers/SalesHelper";

const StyledDialog = styled(Dialog)(() => ({
  "& .MuiDialog-paper": {
    borderRadius: "16px",
    padding: "16px 0",
    width: "400px",
    maxWidth: "100%",
  },
  "& .MuiDialogContent-root": {
    padding: "0 24px 24px",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": {
      display: "none",
    },
  },
}));

const TaskList = styled("ul")({
  listStyle: "none",
  padding: 0,
  margin: 0,
});

const TaskItem = styled("li")(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "12px 16px",
  borderBottom: "1px solid #f0f0f0",
  "&:last-child": {
    borderBottom: "none",
  },
  "& .label": {
    fontSize: "16px",
    fontWeight: 500,
    color: "#212121",
  },
  "& .status-icon": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
  },
}));

const StatusIcon = ({ status }) => {
  if (status === "loading") {
    return <CircularProgress size={20} sx={{ color: "#FF851B" }} />;
  }
  if (status === "success") {
    return <CheckIcon width={24} height={24} style={{ fill: "#4CAF50" }} />;
  }
  if (status === "error") {
    return <CloseIcon width={24} height={24} style={{ fill: "#F44336" }} />;
  }
  return null;
};

function buildCoreSyncTasks() {
  return [
    { id: "products", label: "Products", status: "pending", fn: () => syncInitialProductsOnly(true) },
    { id: "categories", label: "Categories", status: "pending", fn: () => syncInitialCategoriesOnly(true) },
    { id: "addonMappings", label: "Addon Mappings", status: "pending", fn: () => syncAddonMappings(true) },
    {
      id: "paymentModes",
      label: "Payment Modes",
      status: "pending",
      fn: () => ShopService.getPaymentModes({ useCache: false }),
    },
    {
      id: "invoiceFormat",
      label: "Invoice Format",
      status: "pending",
      fn: () => loadInvoiceFormatForTerminal(getCurrentTerminalId()),
    },
    {
      id: "shopProfile",
      label: "Shop Profile",
      status: "pending",
      fn: async () => {
        const profile = await ShopService.loadShopProfile(false);
        if (profile) {
          localStorage.setItem(STORAGE_KEYS.TAX_PREFERENCE, profile.tax_preference);
          localStorage.setItem(STORAGE_KEYS.TAX_REGION, profile.tax_region);
        }
      },
    },
    {
      id: "terminalSettings",
      label: "Terminal Settings",
      status: "pending",
      fn: () => syncTerminalSettings(getCurrentTerminalId()),
    },
    { id: "customers", label: "Customers", status: "pending", fn: () => CustomerHelper.loadInitialCustomers(true) },
    { id: "salespersons", label: "Salespersons", status: "pending", fn: () => loadInitialSalespersons(true) },
  ];
}

function buildPremiumSyncTasks(hasFullKotValidity) {
  return [
    { id: "priceCategories", label: "Price Categories", status: "pending", fn: () => syncPriceCategories(true) },
    { id: "priceCategoryItems", label: "Price Category Items", status: "pending", fn: () => syncPriceCategoryItems(true) },
    { id: "charges", label: "Charges", status: "pending", fn: () => syncCharges(true) },
    { id: "chargeGroups", label: "Charge Groups", status: "pending", fn: () => syncChargeGroups(true) },
    hasFullKotValidity && {
      id: "areasTables",
      label: "Tables & Areas",
      status: "pending",
      fn: () => loadInitialAreasAndTables(true),
    },
    { id: "expenseTypes", label: "Expense Types", status: "pending", fn: () => ExpenseHelper.syncExpenseTypes() },
  ].filter(Boolean);
}

function buildSyncTaskList(isPremium, hasFullKotValidity) {
  const offlineSyncTask = {
    id: "offlineSync",
    label: "Syncing local changes",
    status: "pending",
    fn: async () => {
      await CustomerHelper.syncPendingCustomers();
      await syncPendingSales();
    },
  };

  const coreTasks = buildCoreSyncTasks();
  const premiumTasks = isPremium ? buildPremiumSyncTasks(hasFullKotValidity) : [];
  const otherTasks = [...coreTasks, ...premiumTasks];

  return { offlineSyncTask, otherTasks };
}

export default function SyncDialog({ open, onClose }) {
  const [tasks, setTasks] = useState([]);
  const { selectedShop, hasFullKotValidity } = useContext(ShopContext);
  const syncStarted = useRef(false);

  const isPremium = ShopsHelper.isPremiumShop(selectedShop);

  useEffect(() => {
    if (!open) {
      setTasks([]);
      syncStarted.current = false;
      return;
    }

    if (syncStarted.current) return;
    syncStarted.current = true;

    const runSync = async () => {
      const { offlineSyncTask, otherTasks } = buildSyncTaskList(isPremium, hasFullKotValidity);
      const allTasks = [offlineSyncTask, ...otherTasks];
      setTasks(allTasks.map(({ id, label, status }) => ({ id, label, status })));

      const updateTaskStatus = (id, status) => {
        setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, status } : t)));
      };

      const syncItem = async (id, fn) => {
        try {
          updateTaskStatus(id, "loading");
          await fn();
          updateTaskStatus(id, "success");
        } catch (error) {
          console.error(`Sync failed for ${id}`, error);
          updateTaskStatus(id, "error");
        }
      };

      await syncItem(offlineSyncTask.id, offlineSyncTask.fn);
      await Promise.all(otherTasks.map((task) => syncItem(task.id, task.fn)));

      setTimeout(() => {
        onClose();
        window.location.reload();
      }, 1500);
    };

    runSync();
  }, [open, hasFullKotValidity, isPremium, onClose]);

  const allCompleted = tasks.length > 0 && tasks.every((t) => t.status !== "loading" && t.status !== "pending");

  return (
    <StyledDialog open={open} onClose={allCompleted ? onClose : undefined}>
      <DialogTitle sx={{ fontWeight: "bold", textAlign: "center", paddingBottom: "8px" }}>Syncing Data</DialogTitle>
      <DialogContent>
        <TaskList>
          {tasks.map((task) => (
            <TaskItem key={task.id} status={task.status}>
              <span className="label">{task.label}</span>
              <div className="status-icon">
                <StatusIcon status={task.status} />
              </div>
            </TaskItem>
          ))}
        </TaskList>
      </DialogContent>
    </StyledDialog>
  );
}
